import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import Pagination from '../components/Pagination';

type Reason = 'oversize' | 'content_type' | 'http_error' | 'low_content';
// 'queued' = marked by "Delete all matching" and awaiting the background
// purge (trash:process-queued). Rows leave it for 'deleted' as they drain.
type Status = 'pending' | 'kept' | 'deleted' | 'queued';

type TrashRow = {
  id: number;
  snapshot_id: number;
  archive_id: number;
  archive_domain: string;
  url: string;
  content_type: string | null;
  size_bytes: number;
  status_code: number;
  captured_at: string;
  reason: Reason;
  status: Status;
  detected_at: string;
  decided_at: string | null;
};

type ListResponse = {
  data: TrashRow[];
  meta: { total: number; bytes: number; limit: number; offset: number };
};

type Bucket = { n: number; bytes: number };

type SummaryResponse = {
  by_status: Record<string, { status: Status } & Bucket>;
  by_reason: Array<{ reason: Reason } & Bucket>;
  by_content_type: Array<{ content_type: string | null } & Bucket>;
  by_archive: Array<{ archive_id: number; domain: string } & Bucket>;
  scan: { cursor: number; max_snapshot_id: number; complete: boolean; percent: number };
  policy: {
    oversize_bytes: number;
    denied_content_types: string[];
    low_content_bytes: number;
    flag_http_errors: boolean;
  };
};

const PAGE_SIZE = 50;

const REASON_LABEL: Record<Reason, string> = {
  oversize: 'Oversize',
  content_type: 'Denied type',
  http_error: 'Non-2xx body',
  low_content: 'Low content',
};

/** Our stored copy of the row, not the live site. Same shape the tree,
 *  snapshot table and search results use — (archive, captured_at, url). */
function replayUrl(r: TrashRow): string {
  return `/api/replay/${r.archive_id}/${encodeURIComponent(r.captured_at)}/${encodeURIComponent(r.url)}`;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export default function Trash() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<ListResponse | null>(null);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = (params.get('status') ?? 'pending') as Status;
  const reason = params.get('reason') ?? '';
  const contentType = params.get('content_type') ?? '';
  const archiveId = params.get('archive_id') ?? '';
  const q = params.get('q') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') | 0);

  const loadSummary = useCallback(() => {
    // Facets in the response (by_reason / by_content_type / by_archive) are
    // filtered by every *other* active filter, so passing these narrows the
    // dropdowns to what's actually reachable — e.g. picking "oversize"
    // shrinks the archive dropdown to archives that have an oversize row.
    const query: Record<string, string> = { status };
    if (reason) query.reason = reason;
    if (contentType) query.content_type = contentType;
    if (archiveId) query.archive_id = archiveId;
    if (q) query.q = q;
    api<SummaryResponse>('/trash/summary', { query })
      .then(setSummary)
      .catch(() => { /* header is decorative — a failure here shouldn't blank the table */ });
  }, [status, reason, contentType, archiveId, q]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const query: Record<string, string | number> = {
      status,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (reason) query.reason = reason;
    if (contentType) query.content_type = contentType;
    if (archiveId) query.archive_id = archiveId;
    if (q) query.q = q;
    return api<ListResponse>('/trash', { query })
      .then((resp) => { setData(resp); setSelected(new Set()); })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'))
      .finally(() => setLoading(false));
  }, [status, reason, contentType, archiveId, q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const queuedCount = summary?.by_status?.queued?.n ?? 0;

  // Poll while a bulk deletion drains so the operator sees the queue shrink
  // without reloading. Only while there IS a queue — the rest of the time
  // this page is static and shouldn't be hitting the API on a timer.
  useEffect(() => {
    if (queuedCount === 0) return;
    const t = setInterval(loadSummary, 10000);
    return () => clearInterval(t);
  }, [queuedCount, loadSummary]);

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setParams(next);
  }

  function goToPage(p: number) {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page'); else next.set('page', String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function toggle(id: number) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const rows = data?.data ?? [];
  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  function toggleAll() {
    setSelected(allOnPageSelected ? new Set() : new Set(rows.map((r) => r.id)));
  }

  const selectedBytes = rows
    .filter((r) => selected.has(r.id))
    .reduce((sum, r) => sum + r.size_bytes, 0);

  /**
   * Queue every row matching the current filters, not just the current page.
   * Sends the count we displayed as `expect`; the API refuses with a 409 if
   * the sweep has added rows since, so the number in the confirm dialog is
   * always the number that actually gets queued.
   */
  async function queueAll() {
    if (total === 0) return;

    const scope = reason ? REASON_LABEL[reason as Reason].toLowerCase() : 'matching';
    const ok = window.confirm(
      `Queue all ${total.toLocaleString()} ${scope} snapshot(s) for deletion, `
      + `reclaiming about ${formatBytes(data?.meta.bytes ?? 0)}?\n\n`
      + 'This covers every row matching the current filters, not just this page. '
      + 'They are purged in the background over the next while — you can watch '
      + 'progress under the "Queued" status. It cannot be undone.',
    );
    if (!ok) return;

    setBusy(true);
    try {
      const resp = await api<{ queued: number }>('/trash/queue-all', {
        method: 'POST',
        body: {
          expect: total,
          ...(reason ? { reason } : {}),
          ...(contentType ? { content_type: contentType } : {}),
          ...(archiveId ? { archive_id: archiveId } : {}),
          ...(q ? { q } : {}),
        },
      });
      window.alert(`${resp.queued.toLocaleString()} snapshot(s) queued. They will be purged in the background.`);
      await load();
      loadSummary();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not queue the deletion.');
    } finally {
      setBusy(false);
    }
  }

  async function act(kind: 'keep' | 'delete') {
    const ids = [...selected];
    if (ids.length === 0) return;

    if (kind === 'delete') {
      const ok = window.confirm(
        `Permanently delete ${ids.length} snapshot(s), reclaiming about ${formatBytes(selectedBytes)}?\n\n`
        + 'This removes the snapshot rows, their search-index entries, and any R2 objects '
        + 'left with no remaining references. It cannot be undone.',
      );
      if (!ok) return;
    }

    setBusy(true);
    try {
      if (kind === 'keep') {
        await api('/trash/keep', { method: 'POST', body: { ids } });
      } else {
        await api('/trash', { method: 'DELETE', body: { ids } });
      }
      await load();
      loadSummary();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  const total = data?.meta.total ?? 0;
  const lastPage = total > 0 ? Math.ceil(total / PAGE_SIZE) : 1;
  const scan = summary?.scan;
  const policy = summary?.policy;

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Trash</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Snapshots flagged by the background sweep for review. Nothing here is
          deleted until you delete it. Removing a row purges the snapshot, its
          search-index entries, and any R2 object left with no other reference —
          storage is content-addressed, so a body shared with another snapshot
          keeps its bytes.
        </p>

        {scan && (
          <p className="muted" style={{ fontSize: 13 }}>
            {scan.complete
              ? 'Sweep complete — only newly-captured snapshots are scanned from here.'
              : `Initial sweep ${scan.percent}% complete (snapshot ${scan.cursor.toLocaleString()} of ${scan.max_snapshot_id.toLocaleString()}). Counts below will keep rising until it finishes.`}
          </p>
        )}

        {queuedCount > 0 && (
          <p className="muted" style={{ fontSize: 13 }}>
            <strong>{queuedCount.toLocaleString()}</strong> snapshot(s) queued for deletion
            {' '}({formatBytes(summary?.by_status?.queued?.bytes ?? 0)}) — the background purge
            works through them a batch at a time. This panel refreshes every 10 seconds while
            the queue drains.
          </p>
        )}

        {policy && (
          <p className="muted" style={{ fontSize: 12 }}>
            Current policy: oversize &gt; {formatBytes(policy.oversize_bytes)}
            {policy.denied_content_types.length > 0 && <> · denied types <code>{policy.denied_content_types.join(', ')}</code></>}
            {policy.low_content_bytes > 0 && <> · HTML under {formatBytes(policy.low_content_bytes)}</>}
            {policy.flag_http_errors && <> · stored non-2xx bodies</>}
          </p>
        )}

        {summary && summary.by_reason.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
            {summary.by_reason.map((b) => (
              <button
                key={b.reason}
                className="secondary"
                onClick={() => updateFilter('reason', reason === b.reason ? '' : b.reason)}
                style={{ fontWeight: reason === b.reason ? 700 : 400 }}
                title={`${b.n.toLocaleString()} pending`}
              >
                {REASON_LABEL[b.reason]}: {formatBytes(b.bytes)} ({b.n.toLocaleString()})
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          <input
            type="search"
            placeholder="Filter by URL…"
            defaultValue={q}
            onKeyDown={(e) => { if (e.key === 'Enter') updateFilter('q', (e.target as HTMLInputElement).value); }}
            style={{ flex: '1 1 240px', minWidth: 0 }}
          />
          <select value={status} onChange={(e) => updateFilter('status', e.target.value)}>
            <option value="pending">Pending review</option>
            <option value="queued">Queued for deletion</option>
            <option value="kept">Kept</option>
            <option value="deleted">Deleted</option>
          </select>
          <select value={reason} onChange={(e) => updateFilter('reason', e.target.value)}>
            <option value="">Any reason</option>
            {(Object.keys(REASON_LABEL) as Reason[]).map((r) => (
              <option key={r} value={r}>{REASON_LABEL[r]}</option>
            ))}
          </select>
          <select value={contentType} onChange={(e) => updateFilter('content_type', e.target.value)}>
            <option value="">Any content type</option>
            {(summary?.by_content_type ?? []).map((c) => (
              <option key={c.content_type ?? ''} value={c.content_type ?? ''}>
                {(c.content_type ?? '(none)')} — {formatBytes(c.bytes)}
              </option>
            ))}
          </select>
          <select value={archiveId} onChange={(e) => updateFilter('archive_id', e.target.value)}>
            <option value="">Any archive</option>
            {(summary?.by_archive ?? []).map((a) => (
              <option key={a.archive_id} value={String(a.archive_id)}>
                {a.domain} — {formatBytes(a.bytes)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="card">
        {error && <p className="error">{error}</p>}
        {loading && <p className="muted">Loading…</p>}

        {!loading && rows.length === 0 && (
          <p className="muted">
            Nothing {status === 'pending' ? 'awaiting review' : status}.
            {scan && !scan.complete && ' The sweep is still running.'}
          </p>
        )}

        {rows.length > 0 && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll} />
                Select page
              </label>
              <span className="muted">
                {selected.size > 0
                  ? `${selected.size} selected · ${formatBytes(selectedBytes)}`
                  : `${total.toLocaleString()} rows · ${formatBytes(data?.meta.bytes ?? 0)} total`}
              </span>
              <span style={{ flex: 1 }} />
              {status === 'pending' && (
                <>
                  <button
                    className="secondary"
                    disabled={busy || selected.size === 0}
                    onClick={() => act('keep')}
                  >
                    Keep
                  </button>
                  <button
                    className="secondary"
                    style={{
                      background: 'var(--pill-bg-error)',
                      color: 'var(--pill-fg-error)',
                      borderColor: 'var(--pill-border-stalled)',
                    }}
                    disabled={busy || selected.size === 0}
                    onClick={() => act('delete')}
                    title="Permanently delete the selected snapshots and their unreferenced R2 objects"
                  >
                    {busy ? 'Working…' : `Delete${selected.size > 0 ? ` (${selected.size})` : ''}`}
                  </button>
                  {/* Acts on the whole filtered set, so it stays enabled with
                      nothing selected — that's the point of it. */}
                  <button
                    className="secondary"
                    style={{
                      background: 'var(--pill-bg-error)',
                      color: 'var(--pill-fg-error)',
                      borderColor: 'var(--pill-border-stalled)',
                    }}
                    disabled={busy || total === 0}
                    onClick={queueAll}
                    title="Queue every row matching the current filters for background deletion"
                  >
                    {`Delete all ${total.toLocaleString()}`}
                  </button>
                </>
              )}
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th />
                    <th>Size</th>
                    <th>Reason</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Archive</th>
                    <th>URL</th>
                    <th>Captured</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>
                        {status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                          />
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatBytes(r.size_bytes)}</td>
                      <td>{REASON_LABEL[r.reason]}</td>
                      <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <code style={{ fontSize: 12 }}>{r.content_type ?? '(none)'}</code>
                      </td>
                      <td>{r.status_code}</td>
                      <td>
                        <Link to={`/archives/${r.archive_id}`}>{r.archive_domain}</Link>
                      </td>
                      <td style={{ maxWidth: 460, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.status === 'deleted' ? (
                          // Our copy is gone. Linking to replay here would
                          // either 404 or — worse — silently resolve to a
                          // *different* capture of the same URL via the
                          // controller's at-or-before / after fallback tiers.
                          <span className="muted" title={r.url}>{r.url}</span>
                        ) : (
                          // rel must allow Referer so Sanctum treats this as
                          // first-party SPA traffic; noopener alone keeps the
                          // target=_blank guarantee.
                          <a
                            href={replayUrl(r)}
                            target="_blank"
                            rel="noopener"
                            title={`View our archived copy — ${r.url}`}
                          >
                            {r.url}
                          </a>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {new Date(r.captured_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              lastPage={lastPage}
              total={total}
              perPage={PAGE_SIZE}
              onChange={goToPage}
            />
          </>
        )}
      </div>
    </>
  );
}
